import {combine, forward, merge, sample} from 'effector';

import {RestError, showRestError} from 'core/errors';
import {initFiltersTable, initSortingsTable} from 'core/hooks/grid2.0';
import {BlockingAccessors} from 'types';
import {mapFilters, mapSortings} from 'utils/grid-mappers';

import {
    $removeLockModalOpened,
    clearDataCacheFx,
    data$,
    defaultFilters,
    defaultSortings,
    dictionary$,
    fetchDataFx,
    fetchDictionaryFx,
    filters$,
    filtersChanged,
    filtersReset,
    Gate,
    gridCacheChanged,
    gridCacheReset,
    gridTokensCache$,
    pageNumber$,
    pageSize$,
    refetchWithSortByDescDate,
    removeLockCode,
    RemoveLockCodeFx,
    search$,
    searchChanged,
    searchReset,
    setRemoveLockModalOpened,
    sortings$,
    sortingsChanged,
    sortingsReset,
    stateReset,
    tableStateCacheChanged,
    tableStateChanged,
} from './index';

data$.on(fetchDataFx.done, (_, {result}) => result).reset(Gate.close);
dictionary$.on(fetchDictionaryFx.doneData, (_, result) => result ?? {}).reset(Gate.close);
pageNumber$.reset(filtersChanged, sortingsChanged, stateReset, searchChanged);
pageSize$.reset(stateReset);
filters$.reset(Gate.close);
search$.reset(Gate.close);

gridTokensCache$
    .on(fetchDataFx.done, (state, {params, result}) => ({
        ...state,
        [params.paging.pageNumber + 1]: result.nextPageRef ?? null,
    }))
    .on(gridCacheReset, () => ({0: null}))
    .on(gridCacheChanged, (state, {pageNumber, token}) => ({
        ...state,
        [pageNumber]: token,
    }))
    .reset(stateReset, Gate.close, filtersChanged, sortingsChanged, searchChanged);

initFiltersTable(filters$, filtersChanged, filtersReset, defaultFilters, stateReset);
initSortingsTable(sortings$, sortingsChanged, sortingsReset, defaultSortings, stateReset);

forward({
    from: tableStateCacheChanged,
    to: clearDataCacheFx,
});

sample({
    clock: refetchWithSortByDescDate,
    fn: () => [{id: 'createdAt', value: true}],
    target: sortingsChanged,
});

forward({
    from: refetchWithSortByDescDate,
    to: searchReset,
});

const gridFetchSource = combine({
    sortings: sortings$,
    pageNumber: pageNumber$,
    pageSize: pageSize$,
    filters: filters$,
    search: search$,
    cache: gridTokensCache$,
});

const mapGridFetchParams = ({
    pageNumber,
    pageSize,
    filters,
    sortings,
    search,
    cache,
}: {
    pageNumber: number;
    pageSize: number;
    filters: Parameters<typeof mapFilters>[0];
    sortings: Parameters<typeof mapSortings>[0];
    search: string;
    cache: Record<number, string | null>;
}) => ({
    paging: {
        pageNumber,
        pageSize,
    },
    filters: mapFilters(filters),
    sortings: mapSortings(sortings),
    search,
    pageRef: cache?.[pageNumber] ?? null,
});

const createdAtFilter$ = filters$.map((filters) => filters[BlockingAccessors.createdDate]);

sample({
    clock: merge([Gate.open, tableStateChanged, searchChanged]),
    source: gridFetchSource,
    fn: mapGridFetchParams,
    target: fetchDataFx,
});

sample({
    clock: merge([Gate.open, createdAtFilter$]),
    source: gridFetchSource,
    fn: mapGridFetchParams,
    target: fetchDictionaryFx,
});

fetchDataFx.fail.watch(({error}) => showRestError(error as RestError));
fetchDictionaryFx.fail.watch(({error}) => showRestError(error as RestError));

sample({
    clock: removeLockCode,
    target: RemoveLockCodeFx,
});

sample({
    clock: setRemoveLockModalOpened,
    target: $removeLockModalOpened,
});

sample({
    clock: RemoveLockCodeFx.done,
    fn: () => null,
    target: setRemoveLockModalOpened,
});
